import { useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

import * as Sentry from "@sentry/browser";

import { toast } from "@/design-system/components/Toast/Toast";

import { makeRequest } from "./util";

import { invalidateEventDefs } from "./api/useEventDefs";
import useListAllDestinationsForEnv from "./destinations/v2/hooks/useListAllDestinationsForEnv";

const oauthReturnDestinationIdKey = (integrationName) =>
  `destination_oauth_return_id:${integrationName}`;

export function storeOAuthReturnDestinationId(integrationName, destinationId) {
  sessionStorage.setItem(
    oauthReturnDestinationIdKey(integrationName),
    destinationId,
  );
}

function clearOAuthReturnDestinationId(integrationName) {
  sessionStorage.removeItem(oauthReturnDestinationIdKey(integrationName));
}

function consumeOAuthReturnURL(destinationURL, integrationName) {
  const key = oauthReturnDestinationIdKey(integrationName);
  const destinationId = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  return destinationId ? `${destinationURL}/${destinationId}` : destinationURL;
}

function decodeOAuthErrorDescription(description) {
  try {
    return decodeURIComponent(description.replace(/\+/g, " "));
  } catch {
    return description;
  }
}

export const DestinationOAuth = ({ destinationURL, destinationName }) => {
  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();
  const params = new URLSearchParams(useLocation().search);
  const history = useHistory();

  const [completeOauthRequested, setCompleteOauthRequested] = useState(false);
  if (completeOauthRequested) {
    return null;
  }

  const buildErrorURL = () =>
    `${consumeOAuthReturnURL(destinationURL, destinationName)}?error=Failed+to+complete+OAuth`;

  const failOAuth = (err, userMessage) => {
    setCompleteOauthRequested(true);
    console.error(err);
    Sentry.captureException(err);
    toast.error(userMessage);
    history.push(buildErrorURL());
  };

  const oauthError = params.get("error");
  if (oauthError) {
    const rawDescription =
      params.get("error_description") || params.get("error") || oauthError;
    const description = decodeOAuthErrorDescription(rawDescription);
    failOAuth(
      new Error(`OAuth provider returned error: ${description}`),
      `Failed to complete OAuth: ${description}`,
    );
    return null;
  }

  if (!params.get("state")) {
    failOAuth(
      new Error("Unable to find 'state' parameter for OAuth."),
      "Failed to complete OAuth",
    );
    return null;
  }

  if (!params.get("code")) {
    failOAuth(
      new Error("Unable to find 'code' parameter for OAuth."),
      "Failed to complete OAuth",
    );
    return null;
  }

  setCompleteOauthRequested(true);

  // if code last two chars are #_, then remove those for reddit-ads
  if (destinationName === "reddit-ads") {
    const code = params.get("code");
    if (code.slice(-2) === "#_") {
      params.set("code", code.slice(0, -2));
    }
  }

  const request = {
    integration_name: destinationName,
    state: params.get("state"),
    code: params.get("code"),
  };

  makeRequest("complete_oauth_flow", request)
    .then((response) => {
      if (!destinationURL) {
        window.close();
      } else {
        if (response.data.destination_id) {
          clearOAuthReturnDestinationId(destinationName);
          invalidateEventDefs();
          refetchDestinations();
          history.push(`${destinationURL}/${response.data.destination_id}`);
        } else {
          history.push(consumeOAuthReturnURL(destinationURL, destinationName));
        }
      }
    })
    .catch((err) => {
      console.error(err);
      toast.error("Failed to complete OAuth");
      Sentry.captureException(err);
      history.push(buildErrorURL());
    });

  return null;
};
